import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import ModalController from '../modal_controller'
import {
  trans, DELETE_CRITICAL_TITLE, DELETE_CRITICAL_TEXT, DELETE_SEVERE_TITLE, DELETE_SEVERE_TEXT, DELETE_MODERATE_TITLE,
  DELETE_MODERATE_TEXT, DELETE_PARENT_TITLE, DELETE_PARENT_TEXT, ERROR_UNKNOWN, SEVERE_NAME, CRITICAL_NAME,
  MODERATE_NAME
} from '../../translator'

export default class extends ModalController {
  static values = {
    threatCategoryId: String,
    threatCategoryName: String,
    threatCategoryChildren: Array
  }

  static targets = [
    'downArrow', 'rightArrow', 'error',
    'addCriticalButton', 'deleteCriticalButton', 'addSevereButton', 'deleteSevereButton', 'addModerateButton', 'deleteModerateButton',
    'modalTitle', 'modalText'
  ]

  showHideChildren () {
    this.downArrowTarget.classList.toggle('d-none')
    this.rightArrowTarget.classList.toggle('d-none')
    this.threatCategoryChildrenValue.forEach(child => {
      const childElement = document.getElementById(child)
      childElement.classList.toggle('d-none')
    })
  }

  addCriticalThreatCategory () {
    fetchWithHandlingLoginRedirect(
      '/internal/api/threat-categories',
      {
        method: 'POST',
        body: JSON.stringify({
          name: trans(CRITICAL_NAME, { '%name%': this.threatCategoryNameValue }, 'threat-categories'),
          parentId: this.threatCategoryIdValue,
          threatLevel: 'critical',
          priority: 3
        })
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        return response.json()
      })
      .then(data => {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = data.renderedThreatCategory
        const newThreatCategory = tempDiv.firstChild

        this.element.insertAdjacentElement('afterend', newThreatCategory)

        const children = this.threatCategoryChildrenValue
        children.splice(0, 0, newThreatCategory.id)
        this.threatCategoryChildrenValue = children

        this.addCriticalButtonTarget.classList.add('d-none')
        this.deleteCriticalButtonTarget.dataset.threatCategoryId = newThreatCategory.id
        this.deleteCriticalButtonTarget.classList.remove('d-none')
      })
      .catch(() => {
        this.displayError()
      })
  }

  addSevereThreatCategory () {
    fetchWithHandlingLoginRedirect(
      '/internal/api/threat-categories',
      {
        method: 'POST',
        body: JSON.stringify({
          name: trans(SEVERE_NAME, { '%name%': this.threatCategoryNameValue }, 'threat-categories'),
          parentId: this.threatCategoryIdValue,
          threatLevel: 'severe',
          priority: 2
        })
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        return response.json()
      })
      .then(data => {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = data.renderedThreatCategory
        const newThreatCategory = tempDiv.firstChild

        const children = this.threatCategoryChildrenValue
        let inserted = false
        for (let i = this.threatCategoryChildrenValue.length - 1; i >= 0; i--) {
          const child = this.threatCategoryChildrenValue[i]
          const childElement = document.getElementById(child)
          if (childElement.dataset.threatLevel === 'critical') {
            childElement.insertAdjacentElement('afterend', newThreatCategory)
            children.splice(1, 0, newThreatCategory.id)
            inserted = true
            break
          }
        }

        if (!inserted) {
          this.element.insertAdjacentElement('afterend', newThreatCategory)
          children.splice(0, 0, newThreatCategory.id)
        }
        this.threatCategoryChildrenValue = children

        this.addSevereButtonTarget.classList.add('d-none')
        this.deleteSevereButtonTarget.dataset.threatCategoryId = newThreatCategory.id
        this.deleteSevereButtonTarget.classList.remove('d-none')
      })
      .catch(() => {
        this.displayError()
      })
  }

  addModerateThreatCategory () {
    fetchWithHandlingLoginRedirect(
      '/internal/api/threat-categories',
      {
        method: 'POST',
        body: JSON.stringify({
          name: trans(MODERATE_NAME, { '%name%': this.threatCategoryNameValue }, 'threat-categories'),
          parentId: this.threatCategoryIdValue,
          threatLevel: 'moderate',
          priority: 1
        })
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        return response.json()
      })
      .then(data => {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = data.renderedThreatCategory
        const newThreatCategory = tempDiv.firstChild

        const children = this.threatCategoryChildrenValue
        let inserted = false
        for (let i = this.threatCategoryChildrenValue.length - 1; i >= 0; i--) {
          const child = this.threatCategoryChildrenValue[i]
          const childElement = document.getElementById(child)
          if (childElement.dataset.threatLevel === 'severe') {
            childElement.insertAdjacentElement('afterend', newThreatCategory)
            children.splice(2, 0, newThreatCategory.id)
            inserted = true
            break
          } else if (childElement.dataset.threatLevel === 'critical') {
            childElement.insertAdjacentElement('afterend', newThreatCategory)
            children.splice(1, 0, newThreatCategory.id)
            inserted = true
            break
          }
        }

        if (!inserted) {
          this.element.insertAdjacentElement('afterend', newThreatCategory)
          children.splice(0, 0, newThreatCategory.id)
        }
        this.threatCategoryChildrenValue = children

        this.addModerateButtonTarget.classList.add('d-none')
        this.deleteModerateButtonTarget.dataset.threatCategoryId = newThreatCategory.id
        this.deleteModerateButtonTarget.classList.remove('d-none')
      })
      .catch(error => {
        this.displayError(error)
      })
  }

  openModal (event) {
    switch (event.currentTarget.dataset.threatLevel) {
      case 'critical': {
        this.modalTitleTarget.innerHTML = trans(DELETE_CRITICAL_TITLE, {}, 'threat-categories')
        this.modalTextTarget.innerHTML = trans(DELETE_CRITICAL_TEXT, { '%category%': this.threatCategoryNameValue }, 'threat-categories')
        break
      }
      case 'severe': {
        this.modalTitleTarget.innerHTML = trans(DELETE_SEVERE_TITLE, {}, 'threat-categories')
        this.modalTextTarget.innerHTML = trans(DELETE_SEVERE_TEXT, { '%category%': this.threatCategoryNameValue }, 'threat-categories')
        break
      }
      case 'moderate': {
        this.modalTitleTarget.innerHTML = trans(DELETE_MODERATE_TITLE, {}, 'threat-categories')
        this.modalTextTarget.innerHTML = trans(DELETE_MODERATE_TEXT, { '%category%': this.threatCategoryNameValue }, 'threat-categories')
        break
      }
      case 'parent': {
        this.modalTitleTarget.innerHTML = trans(DELETE_PARENT_TITLE, { '%category%': this.threatCategoryNameValue }, 'threat-categories')
        this.modalTextTarget.innerHTML = trans(DELETE_PARENT_TEXT, {}, 'threat-categories')
        break
      }
    }

    this.modalTarget.dataset.threatCategoryId = event.currentTarget.dataset.threatCategoryId
    this.modalTarget.dataset.threatLevel = event.currentTarget.dataset.threatLevel

    super.openModal()
  }

  confirmDeleteThreatCategory (event) {
    switch (this.modalTarget.dataset.threatLevel) {
      case 'critical': {
        this.deleteCriticalThreatCategory(this.modalTarget.dataset.threatCategoryId)
        break
      }
      case 'severe': {
        this.deleteSevereThreatCategory(this.modalTarget.dataset.threatCategoryId)
        break
      }
      case 'moderate': {
        this.deleteModerateThreatCategory(this.modalTarget.dataset.threatCategoryId)
        break
      }
      case 'parent': {
        this.deleteParentThreatCategory(this.modalTarget.dataset.threatCategoryId)
        break
      }
    }

    super.closeModal()
  }

  deleteParentThreatCategory (threatCategoryId) {
    fetchWithHandlingLoginRedirect(
      `/internal/api/threat-categories/${threatCategoryId}`,
      {
        method: 'DELETE'
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        this.element.remove()
        this.threatCategoryChildrenValue.forEach(child => {
          const childElement = document.getElementById(child)
          childElement.remove()
        })
      })
      .catch(error => {
        this.displayError(error)
      })
  }

  deleteCriticalThreatCategory (threatCategoryId) {
    fetchWithHandlingLoginRedirect(
      `/internal/api/threat-categories/${threatCategoryId}`,
      {
        method: 'DELETE'
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        const childElement = document.getElementById(threatCategoryId)
        childElement.remove()
        this.threatCategoryChildrenValue = this.threatCategoryChildrenValue.filter(child => child !== threatCategoryId)

        this.addCriticalButtonTarget.classList.remove('d-none')
        this.deleteCriticalButtonTarget.dataset.threatCategoryId = null
        this.deleteCriticalButtonTarget.classList.add('d-none')
      })
      .catch(error => {
        this.displayError(error)
      })
  }

  deleteSevereThreatCategory (threatCategoryId) {
    fetchWithHandlingLoginRedirect(
      `/internal/api/threat-categories/${threatCategoryId}`,
      {
        method: 'DELETE'
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        const childElement = document.getElementById(threatCategoryId)
        childElement.remove()
        this.threatCategoryChildrenValue = this.threatCategoryChildrenValue.filter(child => child !== threatCategoryId)

        this.addSevereButtonTarget.classList.remove('d-none')
        this.deleteSevereButtonTarget.dataset.threatCategoryId = null
        this.deleteSevereButtonTarget.classList.add('d-none')
      })
      .catch(error => {
        this.displayError(error)
      })
  }

  deleteModerateThreatCategory (threatCategoryId) {
    fetchWithHandlingLoginRedirect(
      `/internal/api/threat-categories/${threatCategoryId}`,
      {
        method: 'DELETE'
      })
      .then(response => {
        if (!response.ok) {
          return Promise.reject(response)
        }

        const childElement = document.getElementById(threatCategoryId)
        childElement.remove()
        this.threatCategoryChildrenValue = this.threatCategoryChildrenValue.filter(child => child !== threatCategoryId)

        this.addModerateButtonTarget.classList.remove('d-none')
        this.deleteModerateButtonTarget.dataset.threatCategoryId = null
        this.deleteModerateButtonTarget.classList.add('d-none')
      })
      .catch(() => {
        this.displayError()
      })
  }

  displayError () {
    this.errorTarget.innerHTML = trans(ERROR_UNKNOWN, {}, 'threat-categories')
    this.errorTarget.classList.remove('d-none')
    setTimeout(() => {
      this.errorTarget.classList.add('d-none')
    }, 2000)
  }
}
